import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPalette, faPlus, faTrash, faSave, faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons';
import { adminAPI } from '../../services/api';

// Admin manager for the device-model → colors lookup that drives the Color
// dropdown on the seller's listing form. Colors are model-specific (an
// iPhone 13 Pro's lineup is not an iPhone 15's), so this is a mapping table
// the AutoboyExpress team fills in per model rather than one global list.
const DeviceColorManager = ({ isDarkMode, showToast }) => {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // mapping being edited, or null for new
  const [form, setForm] = useState({ model: '', colors: '' });

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getDeviceColorMappings();
      const list = res?.data?.mappings || res?.mappings || [];
      setMappings(Array.isArray(list) ? list : []);
    } catch {
      showToast?.('Failed to load device colors', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ model: '', colors: '' }); setShowForm(true); };
  const openEdit = (m) => { setEditing(m); setForm({ model: m.model, colors: (m.colors || []).join(', ') }); setShowForm(true); };

  const submit = async () => {
    const colors = form.colors.split(',').map(c => c.trim()).filter(Boolean);
    if (!form.model.trim()) return showToast?.('Model is required', 'error');
    if (colors.length === 0) return showToast?.('Add at least one color', 'error');
    setSaving(true);
    try {
      if (editing) {
        await adminAPI.updateDeviceColorMapping(editing.id || editing._id, { model: form.model.trim(), colors });
        showToast?.('Color mapping updated', 'success');
      } else {
        await adminAPI.createDeviceColorMapping({ model: form.model.trim(), colors });
        showToast?.('Color mapping created', 'success');
      }
      setShowForm(false);
      await load();
    } catch (e) {
      showToast?.(e?.message || 'Failed to save mapping — a mapping for this model may already exist', 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete color options for "${m.model}"? Sellers listing this model will fall back to typing a color manually.`)) return;
    try {
      await adminAPI.deleteDeviceColorMapping(m.id || m._id);
      showToast?.('Color mapping deleted', 'success');
      await load();
    } catch {
      showToast?.('Failed to delete mapping', 'error');
    }
  };

  const card = {
    background: isDarkMode ? 'rgba(15,15,20,0.85)' : 'rgba(255,255,255,0.98)',
    border: `1px solid ${isDarkMode ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.12)'}`,
    borderRadius: '20px', padding: '1.75rem', marginTop: '1.5rem',
  };
  const textPrimary = isDarkMode ? '#f1f5f9' : '#1e293b';
  const textSecondary = isDarkMode ? '#94a3b8' : '#64748b';
  const input = {
    width: '100%', padding: '10px 12px', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box',
    border: `1px solid ${isDarkMode ? '#475569' : '#cbd5e1'}`,
    background: isDarkMode ? '#0a0a0a' : '#f8fafc', color: textPrimary, outline: 'none',
  };

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: textPrimary, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FontAwesomeIcon icon={faPalette} style={{ color: '#22C55E' }} /> Device Colors
          </h3>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: textSecondary }}>
            Per-model color options for the seller listing form — {mappings.length} model{mappings.length !== 1 ? 's' : ''} configured
          </p>
        </div>
        <button onClick={openNew}
          style={{ background: 'linear-gradient(135deg, #22C55E, #16A34A)', color: '#fff', border: 'none', borderRadius: '50px', padding: '0.6rem 1.25rem', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <FontAwesomeIcon icon={faPlus} /> Add Model
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: textSecondary }}>
          <FontAwesomeIcon icon={faSpinner} spin /> Loading…
        </div>
      ) : mappings.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: textSecondary, fontSize: '0.9rem' }}>
          No models configured yet. Until a model is added here, sellers listing it type the color in manually.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {mappings.map(m => (
            <div key={m.id || m._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', padding: '0.85rem 1rem', borderRadius: '12px', background: isDarkMode ? 'rgba(255,255,255,0.03)' : '#f8fafc', border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.06)' : '#e2e8f0'}`, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0, flex: '1 1 240px' }}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: textPrimary }}>{m.model}</div>
                <div style={{ fontSize: '0.8rem', color: textSecondary, marginTop: '0.2rem' }}>{(m.colors || []).join(' · ')}</div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                <button onClick={() => openEdit(m)} style={{ padding: '0.45rem 0.9rem', borderRadius: '8px', border: `1px solid ${isDarkMode ? '#475569' : '#cbd5e1'}`, background: 'transparent', color: textSecondary, fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>
                  Edit
                </button>
                <button onClick={() => remove(m)} style={{ padding: '0.45rem 0.9rem', borderRadius: '8px', border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.08)', color: '#ef4444', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div onClick={() => setShowForm(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: isDarkMode ? '#1e293b' : '#fff', borderRadius: '16px', padding: '1.75rem', width: '100%', maxWidth: '460px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: textPrimary }}>
                {editing ? 'Edit Color Options' : 'Add Model Colors'}
              </h3>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: textSecondary }}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem', color: textPrimary }}>Device Model</label>
            <input value={form.model} onChange={e => setForm({ ...form, model: e.target.value })} placeholder="e.g. iPhone 13 Pro" style={input} />
            <div style={{ fontSize: '0.75rem', color: textSecondary, margin: '0.35rem 0 1rem' }}>
              Must match how sellers type the model on their listing (matching ignores case and spacing).
            </div>

            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem', color: textPrimary }}>Colors</label>
            <input value={form.colors} onChange={e => setForm({ ...form, colors: e.target.value })} placeholder="Graphite, Gold, Silver, Sierra Blue" style={input} />
            <div style={{ fontSize: '0.75rem', color: textSecondary, marginTop: '0.35rem' }}>Comma-separated, in the order they should appear in the dropdown.</div>

            <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
              <button onClick={() => setShowForm(false)} style={{ padding: '0.55rem 1.1rem', borderRadius: '8px', border: `1px solid ${isDarkMode ? '#475569' : '#cbd5e1'}`, background: 'transparent', color: textSecondary, fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={submit} disabled={saving} style={{ padding: '0.55rem 1.1rem', borderRadius: '8px', border: 'none', background: '#22C55E', color: '#fff', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? <><FontAwesomeIcon icon={faSpinner} spin /> Saving…</> : <><FontAwesomeIcon icon={faSave} /> Save</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceColorManager;
